import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  IndianRupee, Users, TrendingUp, AlertCircle, Eye, Edit3,
  CheckCircle, XCircle, Clock, Search, Filter, Calendar, Send,
  FileText, ArrowUpRight, ArrowDownRight, BarChart3, X, Check, AlertTriangle,
  Download, Printer, FileSpreadsheet, Table as TableIcon
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import { buildApiUrl } from '../../utils/apiSetup';
import { formatDate } from '../../utils/dateUtils';

const LoansAdvancesManagement = ({ workflowMode }) => {
  const { user, hasPermission } = useAuth();
  const canView = hasPermission?.('loan:view') || hasPermission?.('loan:add') || hasPermission?.('loan:approve') || hasPermission?.('*');
  const canAdd = hasPermission?.('loan:add') || hasPermission?.('*');
  const canApprove = hasPermission?.('loan:approve') || hasPermission?.('*');

  const tier = user?.dashboard_tier || user?.role_dashboard_tier || user?.role?.dashboard_tier;
  const mode = workflowMode || (tier === 'checker' ? 'checker' : tier === 'maker' ? 'maker' : 'admin');
  const canCheckerApprove = mode === 'checker' && canApprove;
  const canMakerSubmit = mode === 'maker' && canAdd;
  const isAdminMode = mode === 'admin';
  const [loans, setLoans] = useState([]);
  const [advances, setAdvances] = useState([]);
  const [portfolioStats, setPortfolioStats] = useState({
    total_outstanding: 0,
    active_loans: 0,
    monthly_collections: 0,
    default_rate: 0,
    total_disbursed: 0,
    recovery_rate: 0
  });
  const [loading, setLoading] = useState(true);
  const { showSuccess, showError } = useNotification();
  const setError = (msg) => { if (msg) showError(msg); };
  const setSuccess = (msg) => { if (msg) showSuccess(msg); };
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [selectedLoanForEdit, setSelectedLoanForEdit] = useState(null);
  const [showFestivalAdvanceModal, setShowFestivalAdvanceModal] = useState(false);
  const [allEmployees, setAllEmployees] = useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [festivalAdvanceForm, setFestivalAdvanceForm] = useState({
    amount: '',
    monthly_recovery: '',
    purpose: 'Festival Advance'
  });
  const [employeeSearchTerm, setEmployeeSearchTerm] = useState('');

  useEffect(() => {
    fetchPortfolioData();
  }, []);

  const fetchPortfolioData = async () => {
    try {
      setLoading(true);
      const [statsResponse, loansResponse, advancesResponse] = await Promise.all([
        fetch(buildApiUrl('/api/v1/benfish-loans/portfolio/stats'), {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`
          }
        }),
        fetch(buildApiUrl('/api/v1/benfish-loans/applications'), {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`
          }
        }),
        fetch(buildApiUrl('/api/v1/benfish-loans/advance-applications'), {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`
          }
        })
      ]);

      if (statsResponse.ok) {
        const stats = await statsResponse.json();
        setPortfolioStats(stats);
      } else {
        console.error('Failed to fetch portfolio stats:', statsResponse.status);
      }

      if (loansResponse.ok) {
        const loansData = await loansResponse.json();
        console.log('Fetched loans data:', loansData.length, 'loans', loansData);
        setLoans(loansData);
      } else {
        console.error('Failed to fetch loans:', loansResponse.status);
        const errorData = await loansResponse.json().catch(() => ({}));
        console.error('Loans error details:', errorData);
      }

      if (advancesResponse.ok) {
        const advancesData = await advancesResponse.json();
        console.log('Fetched advances data:', advancesData.length, 'advances');
        setAdvances(advancesData);
      } else {
        console.error('Failed to fetch advances:', advancesResponse.status);
      }
    } catch (error) {
      console.error('Error fetching portfolio data:', error);
      setError('Failed to load portfolio data. Please check your permissions and try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchAllEmployees = async () => {
    try {
      // Fetch all employees using the search endpoint without filters
      const queryParams = new URLSearchParams({
        limit: 1000,  // Get a large number of employees
        offset: 0
      });

      const response = await fetch(buildApiUrl(`/api/v1/benfish-employees/search?${queryParams}`), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        // Map to include full_name consistently
        const mappedEmployees = (data.employees || []).map(emp => ({
          ...emp,
          full_name: emp.full_name || emp.emp_name
        }));
        setAllEmployees(mappedEmployees);
      }
    } catch (error) {
      console.error('Error fetching employees:', error);
      setError('Failed to fetch employees');
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleApplyFestivalAdvance = async () => {
    if (selectedEmployees.length === 0) {
      setError('Please select at least one employee');
      setTimeout(() => setError(''), 3000);
      return;
    }

    if (!festivalAdvanceForm.amount || !festivalAdvanceForm.monthly_recovery) {
      setError('Please fill in all required fields');
      setTimeout(() => setError(''), 3000);
      return;
    }

    try {
      setLoading(true);
      const amount = parseFloat(festivalAdvanceForm.amount);
      const monthlyRecovery = parseFloat(festivalAdvanceForm.monthly_recovery);
      const recoveryMonths = Math.ceil(amount / monthlyRecovery);

      let successCount = 0;
      let failureCount = 0;

      // Process employees sequentially with a delay to avoid rate limiting
      for (let i = 0; i < selectedEmployees.length; i++) {
        const employeeId = selectedEmployees[i];

        try {
          const response = await fetch(buildApiUrl('/api/v1/benfish-loans/advance-applications'), {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('access_token')}`
            },
            body: JSON.stringify({
              employee_id: employeeId,
              advance_type: 'FESTIVAL',
              advance_amount: amount,
              recovery_months: recoveryMonths,
              purpose: festivalAdvanceForm.purpose
            })
          });

          if (response.ok) {
            successCount++;
          } else {
            failureCount++;
          }

          // Add a small delay between requests to avoid rate limiting (100ms)
          if (i < selectedEmployees.length - 1) {
            await new Promise(resolve => setTimeout(resolve, 100));
          }
        } catch (err) {
          failureCount++;
          console.error(`Failed to create advance for employee ${employeeId}:`, err);
        }

        // Update progress in the UI (optional)
        setSuccess(`Creating festival advances... ${i + 1}/${selectedEmployees.length}`);
      }

      // Show final results
      if (successCount > 0) {
        setSuccess(`Successfully created ${successCount} festival advance(s) in draft status${failureCount > 0 ? `. ${failureCount} failed.` : ''}`);
        setTimeout(() => setSuccess(''), 5000);
        setShowFestivalAdvanceModal(false);
        setSelectedEmployees([]);
        setFestivalAdvanceForm({ amount: '', monthly_recovery: '', purpose: 'Festival Advance' });
        await fetchPortfolioData();
      } else {
        setError(`Failed to create festival advances. All ${failureCount} requests failed.`);
        setTimeout(() => setError(''), 5000);
      }
    } catch (error) {
      setError('Failed to create festival advances: ' + error.message);
      setTimeout(() => setError(''), 5000);
    } finally {
      setLoading(false);
    }
  };

  const handleBulkActivateFestivalAdvances = async () => {
    const draftFestivalAdvances = advances.filter(adv =>
      adv.advance_type === 'Festival Advance' && adv.status === 'draft'
    );

    if (draftFestivalAdvances.length === 0) {
      setError('No draft festival advances found');
      setTimeout(() => setError(''), 3000);
      return;
    }

    if (!window.confirm(`Are you sure you want to activate ${draftFestivalAdvances.length} festival advance(s)?`)) {
      return;
    }

    try {
      setLoading(true);
      const promises = draftFestivalAdvances.map(adv =>
        fetch(buildApiUrl(`/api/v1/benfish-loans/advance-applications/${adv.id}/approve`), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`
          },
          body: JSON.stringify({
            remarks: 'Bulk activation of festival advances'
          })
        })
      );

      const results = await Promise.all(promises);
      const successCount = results.filter(r => r.ok).length;

      if (successCount > 0) {
        setSuccess(`Successfully activated ${successCount} festival advance(s)`);
        setTimeout(() => setSuccess(''), 5000);
        await fetchPortfolioData();
      }
    } catch (error) {
      setError('Failed to activate festival advances: ' + error.message);
      setTimeout(() => setError(''), 5000);
    } finally {
      setLoading(false);
    }
  };


  const StatCard = ({ title, value, subtitle, icon: Icon, color, trend }) => {
    // Check if this is a currency value based on title
    const isCurrency = title.includes('Outstanding') || title.includes('Collections') ||
                      title.includes('Disbursed') || title.includes('₹');

    return (
      <motion.div
        whileHover={{ scale: 1.02 }}
        className="bg-white rounded-xl p-6 shadow-lg border border-gray-100"
      >
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm font-medium text-gray-600 mb-1">{title}</p>
            <div className="text-3xl font-bold text-gray-900 mb-2">
              {typeof value === 'number' && isCurrency ?
                `₹${value.toLocaleString('en-IN')}` : value}
            </div>
          {subtitle && (
            <div className="flex items-center text-sm">
              {trend && (
                trend > 0 ?
                  <ArrowUpRight className="w-4 h-4 text-green-600 mr-1" /> :
                  <ArrowDownRight className="w-4 h-4 text-red-600 mr-1" />
              )}
              <span className={trend > 0 ? 'text-green-600' : trend < 0 ? 'text-red-600' : 'text-gray-500'}>
                {subtitle}
              </span>
            </div>
          )}
        </div>
        <div className={`p-3 rounded-full ${color}`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
      </div>
    </motion.div>
    );
  };

  const LoanCard = ({ loan, type }) => (
    <motion.div
      whileHover={{ scale: 1.01 }}
      onClick={() => setSelectedLoanForEdit({...loan, type})}
      className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
    >
      <div className="flex justify-between items-start mb-3">
        <div>
          <h4 className="font-medium text-gray-900">
            {loan.employee?.full_name || loan.employee_name}
          </h4>
          <p className="text-sm text-gray-600">
            {loan.employee?.emp_code} • {loan.loan_type?.name || loan.advance_type}
          </p>
        </div>
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
          loan.status === 'active' ? 'bg-green-100 text-green-800' :
          loan.status === 'draft' ? 'bg-yellow-100 text-yellow-800' :
          loan.status === 'closed' ? 'bg-gray-100 text-gray-800' :
          'bg-red-100 text-red-800'
        }`}>
          {loan.status}
        </span>
      </div>

      <div className="grid grid-cols-2 gap-4 text-sm mb-3">
        <div>
          <span className="text-gray-600">Amount:</span>
          <div className="font-medium">₹{(loan.principal_amount || loan.advance_amount || 0).toLocaleString()}</div>
        </div>
        <div>
          <span className="text-gray-600">Outstanding:</span>
          <div className="font-medium">₹{(loan.current_balance || loan.balance_amount || 0).toLocaleString()}</div>
        </div>
        <div>
          <span className="text-gray-600">Monthly Principal Recovery:</span>
          <div className="font-medium">₹{(loan.fixed_installment || loan.monthly_recovery || 0).toLocaleString()}</div>
        </div>
        <div>
          <span className="text-gray-600">Remaining:</span>
          <div className="font-medium">{loan.installments_remaining || loan.months_remaining || 0} months</div>
        </div>
      </div>

      <div className="text-xs text-gray-500">
        Applied: {formatDate(loan.application_date)}
      </div>
    </motion.div>
  );


  // Unified Modal for View/Edit/Approve
  const EditLoanModal = ({ loan, onClose, onSave }) => {
    const [editForm, setEditForm] = useState({
      current_balance: '',
      monthly_installment: '',
      interest_rate: '',
      remarks: ''
    });
    const [originalForm, setOriginalForm] = useState({});
    const [saving, setSaving] = useState(false);
    const [approving, setApproving] = useState(false);
    const [rejecting, setRejecting] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [editError, setEditError] = useState('');
    const [adjustmentHistory, setAdjustmentHistory] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);

    // Load adjustment history for this loan (read-only panel)
    useEffect(() => {
      const isAdvance = loan?.advance_type || loan?.type === 'advance';
      if (!loan || isAdvance) return;
      let cancelled = false;
      const loadHistory = async () => {
        setHistoryLoading(true);
        try {
          const res = await fetch(buildApiUrl(`/api/v1/loan-adjustments/history?loan_id=${loan.id}&limit=50`),
            { headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` } },
          );
          if (res.ok) {
            const data = await res.json();
            if (!cancelled) setAdjustmentHistory(data.adjustment_history || []);
          }
        } catch (e) {
          console.warn('Failed to load adjustment history:', e);
        } finally {
          if (!cancelled) setHistoryLoading(false);
        }
      };
      loadHistory();
      return () => { cancelled = true; };
    }, [loan?.id]);

    useEffect(() => {
      if (loan) {
        const isAdvance = loan.advance_type || loan.type === 'advance';
        const initialData = {
          current_balance: (loan.current_balance || loan.balance_amount || 0).toString(),
          monthly_installment: (loan.fixed_installment || loan.monthly_recovery || 0).toString(),
          interest_rate: isAdvance ? '0' : (loan.annual_interest_rate || 0).toString(),
          remarks: ''
        };
        setEditForm(initialData);
        setOriginalForm(initialData);
      }
    }, [loan]);

    // Check if form has changed
    const hasChanges = () => {
      return editForm.current_balance !== originalForm.current_balance ||
             editForm.monthly_installment !== originalForm.monthly_installment ||
             editForm.interest_rate !== originalForm.interest_rate ||
             editForm.remarks !== originalForm.remarks;
    };

    // Auto-calculate installments remaining
    const calculateInstallmentsRemaining = () => {
      const balance = parseFloat(editForm.current_balance) || 0;
      const monthly = parseFloat(editForm.monthly_installment) || 0;
      if (monthly > 0) {
        return Math.ceil(balance / monthly);
      }
      return 0;
    };

    const handleSave = async () => {
      setSaving(true);
      setEditError('');

      try {
        const isAdvance = loan.advance_type || loan.type === 'advance';
        const endpoint = isAdvance
          ? `/api/v1/benfish-loans/advance-applications/${loan.id}`
          : `/api/v1/benfish-loans/applications/${loan.id}`;

        const updateData = {
          current_balance: parseFloat(editForm.current_balance) || 0,
          monthly_installment: parseFloat(editForm.monthly_installment) || 0,
          installments_remaining: calculateInstallmentsRemaining(),
          annual_interest_rate: isAdvance ? 0 : (parseFloat(editForm.interest_rate) || 0),
          admin_remarks: editForm.remarks
        };

        const response = await fetch(endpoint, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`
          },
          body: JSON.stringify(updateData)
        });

        if (response.ok) {
          setSuccess(`${isAdvance ? 'Advance' : 'Loan'} updated successfully!`);
          setTimeout(() => setSuccess(''), 5000);
          await fetchPortfolioData();
          onClose();
        } else {
          const errorData = await response.json();
          setEditError(errorData.detail || 'Failed to update loan');
        }
      } catch (error) {
        setEditError('Network error. Please try again.');
      } finally {
        setSaving(false);
      }
    };

    const handleApprove = async () => {
      if (!canCheckerApprove && !isAdminMode) {
        setEditError('Only checker roles can approve applications.');
        return;
      }
      if (loan.status !== 'pending_checker') {
        setEditError('Application must be forwarded by maker before checker approval.');
        return;
      }
      if (!window.confirm(`Approve this ${loan.advance_type ? 'advance' : 'loan'}?`)) {
        return;
      }

      setApproving(true);
      setEditError('');

      try {
        const isAdvance = loan.advance_type || loan.type === 'advance';
        const endpoint = isAdvance
          ? `/api/v1/benfish-loans/advance-applications/${loan.id}/approve`
          : `/api/v1/benfish-loans/applications/${loan.id}/approve`;

        const response = await fetch(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`
          },
          body: JSON.stringify({
            remarks: editForm.remarks || `Approved by ${user?.username || 'checker'}`
          })
        });

        const data = await response.json();

        if (response.ok) {
          setSuccess(`${isAdvance ? 'Advance' : 'Loan'} approved successfully!`);
          setTimeout(() => setSuccess(''), 5000);
          await fetchPortfolioData();
          onClose();
        } else {
          setEditError(data.detail || 'Failed to approve application');
        }
      } catch (error) {
        setEditError('Network error. Please try again.');
      } finally {
        setApproving(false);
      }
    };

    const handleSubmitForApproval = async () => {
      if (!canMakerSubmit && !isAdminMode) {
        setEditError('Only maker roles can forward applications for checker approval.');
        return;
      }
      if (loan.status !== 'draft') {
        setEditError('Only draft applications can be forwarded.');
        return;
      }
      if (!window.confirm(`Forward this ${loan.advance_type ? 'advance' : 'loan'} to checker for approval?`)) {
        return;
      }

      setSubmitting(true);
      setEditError('');

      try {
        const isAdvance = loan.advance_type || loan.type === 'advance';
        const endpoint = isAdvance
          ? `/api/v1/benfish-loans/advance-applications/${loan.id}/submit`
          : `/api/v1/benfish-loans/applications/${loan.id}/submit`;

        const response = await fetch(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`
          },
          body: JSON.stringify({
            remarks: editForm.remarks || `Submitted by ${user?.username || 'maker'}`
          })
        });

        const data = await response.json();
        if (response.ok) {
          setSuccess(`${isAdvance ? 'Advance' : 'Loan'} forwarded for checker approval.`);
          setTimeout(() => setSuccess(''), 5000);
          await fetchPortfolioData();
          onClose();
        } else {
          setEditError(data.detail || 'Failed to forward application');
        }
      } catch (error) {
        setEditError('Network error. Please try again.');
      } finally {
        setSubmitting(false);
      }
    };

    const handleReject = async () => {
      if (!canCheckerApprove && !isAdminMode) {
        setEditError('Only checker roles can reject applications.');
        return;
      }
      if (loan.status !== 'pending_checker') {
        setEditError('Application must be pending checker approval to reject.');
        return;
      }
      const reason = window.prompt('Please enter the reason for rejection:');
      if (!reason || reason.trim().length < 5) {
        return;
      }

      setRejecting(true);
      setEditError('');

      try {
        const isAdvance = loan.advance_type || loan.type === 'advance';
        const endpoint = isAdvance
          ? `/api/v1/benfish-loans/advance-applications/${loan.id}/reject`
          : `/api/v1/benfish-loans/applications/${loan.id}/reject`;

        const response = await fetch(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`
          },
          body: JSON.stringify({ remarks: reason })
        });

        if (response.ok) {
          setSuccess(`${isAdvance ? 'Advance' : 'Loan'} rejected successfully!`);
          setTimeout(() => setSuccess(''), 5000);
          await fetchPortfolioData();
          onClose();
        } else {
          const errorData = await response.json();
          setEditError(errorData.detail || 'Failed to reject application');
        }
      } catch (error) {
        setEditError('Network error. Please try again.');
      } finally {
        setRejecting(false);
      }
    };

    if (!loan) return null;
    const isAdvance = loan.advance_type || loan.type === 'advance';

    const isDraft = loan.status === 'draft';
    const isPendingChecker = loan.status === 'pending_checker';
    const showMakerActions = isDraft && (canMakerSubmit || (isAdminMode && canAdd));
    const showCheckerActions = isPendingChecker && (canCheckerApprove || (isAdminMode && canApprove));

    return (
      <AnimatePresence>
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="flex justify-between items-center p-6 border-b border-gray-200">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">
                  {isAdvance ? 'Advance' : 'Loan'} Details - {loan.employee?.full_name}
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  Status: <span className={`font-medium ${
                    loan.status === 'draft' ? 'text-yellow-600' :
                    loan.status === 'pending_checker' ? 'text-orange-600' :
                    loan.status === 'approved' ? 'text-blue-600' :
                    loan.status === 'active' ? 'text-green-600' :
                    loan.status === 'closed' ? 'text-gray-600' :
                    'text-red-600'
                  }`}>{loan.status.toUpperCase()}</span>
                </p>
              </div>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6">
              {editError && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-center">
                    <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
                    <p className="text-red-700">{editError}</p>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Current Info */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Loan Information</h3>
                  <div className="space-y-2 text-sm">
                    <div><span className="text-gray-600">Employee:</span> <span className="font-medium">{loan.employee?.full_name}</span></div>
                    <div><span className="text-gray-600">Employee Code:</span> <span className="font-medium">{loan.employee?.emp_code}</span></div>
                    <div><span className="text-gray-600">Type:</span> <span className="font-medium">{loan.loan_type?.name || loan.advance_type}</span></div>
                    <div><span className="text-gray-600">Original Amount:</span> <span className="font-medium">₹{(loan.principal_amount || loan.advance_amount || 0).toLocaleString()}</span></div>
                    <div><span className="text-gray-600">Applied Date:</span> <span className="font-medium">{formatDate(loan.application_date)}</span></div>
                    <div><span className="text-gray-600">Purpose:</span> <span className="font-medium text-xs">{loan.purpose || 'N/A'}</span></div>
                  </div>
                </div>

                {/* Edit Form - Only editable for draft loans */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {isDraft ? 'Edit Loan Terms' : 'Loan Terms (Read-Only)'}
                  </h3>

                  {!isAdvance && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Interest Rate (% p.a.)</label>
                      <input
                        type="number"
                        value={editForm.interest_rate}
                        onChange={(e) => setEditForm(prev => ({...prev, interest_rate: e.target.value}))}
                        className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                        min="0"
                        max="20"
                        step="0.25"
                        disabled={!isDraft}
                      />
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Total Outstanding Amount (₹)</label>
                    <input
                      type="number"
                      value={editForm.current_balance}
                      onChange={(e) => setEditForm(prev => ({...prev, current_balance: e.target.value}))}
                      className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                      min="0"
                      step="100"
                      disabled={!isDraft}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Monthly Principal Recovery Amount (₹)
                    </label>
                    <input
                      type="number"
                      value={editForm.monthly_installment}
                      onChange={(e) => setEditForm(prev => ({...prev, monthly_installment: e.target.value}))}
                      className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                      min="0"
                      step="50"
                      disabled={!isDraft}
                    />
                  </div>

                  {/* Auto-calculated field */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Installments Remaining (Auto-calculated)
                    </label>
                    <input
                      type="number"
                      value={calculateInstallmentsRemaining()}
                      className="w-full p-2 border border-gray-300 rounded-lg bg-gray-100 cursor-not-allowed"
                      disabled
                      readOnly
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Automatically calculated based on Outstanding / Monthly Recovery
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Admin Remarks</label>
                    <textarea
                      value={editForm.remarks}
                      onChange={(e) => setEditForm(prev => ({...prev, remarks: e.target.value}))}
                      className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      rows="3"
                      placeholder="Add notes about changes made..."
                    />
                  </div>
                </div>
              </div>

              {/* Warning */}
              {isDraft && (
                <div className="mt-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="flex items-start">
                    <AlertTriangle className="w-5 h-5 text-yellow-600 mr-2 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-yellow-900">Important</h4>
                      <p className="text-sm text-yellow-700 mt-1">
                        Changes to loan terms will affect payroll calculations. Ensure all modifications are reviewed before approval.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Adjustment History (loans only) */}
              {!isAdvance && (
                <div className="mt-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Adjustment History</h3>
                  {historyLoading ? (
                    <div className="text-sm text-gray-500">Loading…</div>
                  ) : adjustmentHistory.length === 0 ? (
                    <div className="text-sm text-gray-500 italic">No adjustments on record.</div>
                  ) : (
                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-3 py-2 text-left font-medium text-gray-700">Date</th>
                            <th className="px-3 py-2 text-left font-medium text-gray-700">Type</th>
                            <th className="px-3 py-2 text-left font-medium text-gray-700">Payroll Run</th>
                            <th className="px-3 py-2 text-left font-medium text-gray-700">Status</th>
                            <th className="px-3 py-2 text-left font-medium text-gray-700">Reason</th>
                            <th className="px-3 py-2 text-left font-medium text-gray-700">Reference</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {adjustmentHistory.map((adj) => (
                            <tr key={adj.id || adj.adjustment_number}>
                              <td className="px-3 py-2 text-gray-700">{formatDate(adj.requested_at, '—')}</td>
                              <td className="px-3 py-2 text-gray-700">{adj.adjustment_type?.name || adj.adjustment_type_code || '—'}</td>
                              <td className="px-3 py-2 text-gray-700">{adj.payroll_run_id ?? '—'}</td>
                              <td className="px-3 py-2">
                                <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                  adj.status === 'applied' ? 'bg-green-100 text-green-800' :
                                  adj.status === 'approved' ? 'bg-blue-100 text-blue-800' :
                                  adj.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                  adj.status === 'rejected' ? 'bg-red-100 text-red-800' :
                                  'bg-gray-100 text-gray-800'
                                }`}>{adj.status}</span>
                              </td>
                              <td className="px-3 py-2 text-gray-700 max-w-xs truncate" title={adj.reason}>{adj.reason}</td>
                              <td className="px-3 py-2 text-gray-500 text-xs">{adj.reference_document || '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="flex justify-between items-center p-6 border-t border-gray-200">
              <div className="flex space-x-3">
                {showMakerActions && (
                  <button
                    onClick={handleSubmitForApproval}
                    disabled={submitting || saving || approving || rejecting}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center"
                  >
                    {submitting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Forwarding...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Forward for Approval
                      </>
                    )}
                  </button>
                )}
                {showCheckerActions && (
                  <>
                    <button
                      onClick={handleApprove}
                      disabled={approving || saving || rejecting || submitting}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center"
                    >
                      {approving ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Approving...
                        </>
                      ) : (
                        <>
                          <CheckCircle className="w-4 h-4 mr-2" />
                          Approve
                        </>
                      )}
                    </button>
                    <button
                      onClick={handleReject}
                      disabled={rejecting || saving || approving || submitting}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center"
                    >
                      {rejecting ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Rejecting...
                        </>
                      ) : (
                        <>
                          <XCircle className="w-4 h-4 mr-2" />
                          Reject
                        </>
                      )}
                    </button>
                  </>
                )}
                {isDraft && mode === 'checker' && (
                  <span className="text-sm text-gray-500 self-center">Awaiting maker review</span>
                )}
              </div>
              <div className="flex space-x-3">
                <button
                  onClick={onClose}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                  disabled={saving || approving || rejecting}
                >
                  Close
                </button>
                {isDraft && showMakerActions && (
                  <button
                    onClick={handleSave}
                    disabled={saving || approving || rejecting || !hasChanges()}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center"
                  >
                    {saving ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Saving...
                      </>
                    ) : (
                      'Save Changes'
                    )}
                  </button>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      </AnimatePresence>
    );
  };

  // Loans/Advances Content
  const filteredData = [...loans, ...advances].filter(item => {
    if (statusFilter !== 'all' && item.status !== statusFilter) return false;
    if (searchTerm) {
      const name = item.employee?.full_name?.toLowerCase() || '';
      const code = item.employee?.emp_code?.toLowerCase() || '';
      if (!name.includes(searchTerm.toLowerCase()) && !code.includes(searchTerm.toLowerCase())) {
        return false;
      }
    }
    return true;
  });

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  const draftFestivalAdvancesCount = advances.filter(adv =>
    adv.advance_type === 'Festival Advance' && adv.status === 'draft'
  ).length;

  if (!canView) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <div className="bg-white rounded-lg border border-gray-200 p-8 text-center text-gray-500">
          You do not have permission to view Loans & Advances Management.
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Loans & Advances Management</h1>
          <p className="text-gray-600">Comprehensive loan portfolio management and approvals</p>
        </div>
        <div className="flex space-x-3">
          {canAdd && (
            <>
              <button
                onClick={() => {
                  setShowFestivalAdvanceModal(true);
                  fetchAllEmployees();
                }}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center space-x-2"
              >
                <Users className="w-5 h-5" />
                <span>Apply Festival Advance</span>
              </button>
              {draftFestivalAdvancesCount > 0 && (
                <button
                  onClick={handleBulkActivateFestivalAdvances}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center space-x-2"
                >
                  <CheckCircle className="w-5 h-5" />
                  <span>Activate Festival Advances ({draftFestivalAdvancesCount})</span>
                </button>
              )}
            </>
          )}
        </div>
      </div>

      <div className="space-y-6">
        {/* Portfolio Overview */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatCard
            title="Total Outstanding"
            value={portfolioStats.total_outstanding}
            subtitle="Loans + Advances"
            icon={IndianRupee}
            color="bg-gradient-to-r from-blue-500 to-blue-600"
          />
          <StatCard
            title="Active Loans"
            value={portfolioStats.active_loans}
            subtitle="Across all employees"
            icon={Users}
            color="bg-gradient-to-r from-green-500 to-green-600"
          />
          <StatCard
            title="Monthly Collections"
            value={portfolioStats.monthly_collections}
            subtitle="Expected this month"
            icon={TrendingUp}
            color="bg-gradient-to-r from-purple-500 to-purple-600"
          />
          <StatCard
            title="Recovery Rate"
            value={`${portfolioStats.recovery_rate}%`}
            subtitle="Monthly collection ratio"
            icon={CheckCircle}
            color="bg-gradient-to-r from-orange-500 to-orange-600"
          />
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-64">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search by employee name or code..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Status</option>
              <option value="draft">Draft (Maker Review)</option>
              <option value="pending_checker">Pending Checker</option>
              <option value="approved">Approved</option>
              <option value="active">Active</option>
              <option value="closed">Closed</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
        </div>

        {/* Loans and Advances List */}
        <div className="bg-white rounded-lg border border-gray-200">
          <div className="p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Applications ({filteredData.length})
              </h3>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
              {filteredData.map((item, index) => (
                <LoanCard
                  key={`${item.loan_number || item.advance_number || `${item.loan_type?.code ? 'loan' : 'advance'}-${item.id}`}`}
                  loan={item}
                  type={item.loan_type?.code ? 'loan' : 'advance'}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Modal */}
      {selectedLoanForEdit && (
        <EditLoanModal
          loan={selectedLoanForEdit}
          onClose={() => setSelectedLoanForEdit(null)}
          onSave={() => {
            setSelectedLoanForEdit(null);
            fetchPortfolioData();
          }}
        />
      )}

      {/* Festival Advance Modal */}
      {showFestivalAdvanceModal && (
        <AnimatePresence>
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto"
            >
              <div className="flex justify-between items-center p-6 border-b border-gray-200">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Apply Bulk Festival Advance</h2>
                  <p className="text-sm text-gray-600 mt-1">Select employees and specify advance details</p>
                </div>
                <button
                  onClick={() => {
                    setShowFestivalAdvanceModal(false);
                    setSelectedEmployees([]);
                    setEmployeeSearchTerm('');
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="p-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Employee Selection */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">Select Employees</h3>

                    {/* Search */}
                    <div className="mb-3">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                        <input
                          type="text"
                          placeholder="Search employees..."
                          value={employeeSearchTerm}
                          onChange={(e) => setEmployeeSearchTerm(e.target.value)}
                          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        />
                      </div>
                    </div>

                    {/* Select All */}
                    <div className="mb-3 flex items-center">
                      <input
                        type="checkbox"
                        id="selectAll"
                        checked={selectedEmployees.length === allEmployees.filter(emp =>
                          emp.full_name?.toLowerCase().includes(employeeSearchTerm.toLowerCase()) ||
                          emp.emp_code?.toLowerCase().includes(employeeSearchTerm.toLowerCase())
                        ).length && allEmployees.length > 0}
                        onChange={(e) => {
                          const filteredEmployees = allEmployees.filter(emp =>
                            emp.full_name?.toLowerCase().includes(employeeSearchTerm.toLowerCase()) ||
                            emp.emp_code?.toLowerCase().includes(employeeSearchTerm.toLowerCase())
                          );
                          if (e.target.checked) {
                            setSelectedEmployees(filteredEmployees.map(emp => emp.id));
                          } else {
                            setSelectedEmployees([]);
                          }
                        }}
                        className="w-4 h-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                      />
                      <label htmlFor="selectAll" className="ml-2 text-sm font-medium text-gray-700">
                        Select All ({selectedEmployees.length} selected)
                      </label>
                    </div>

                    {/* Employee List */}
                    <div className="border border-gray-200 rounded-lg max-h-96 overflow-y-auto">
                      {allEmployees
                        .filter(emp =>
                          emp.full_name?.toLowerCase().includes(employeeSearchTerm.toLowerCase()) ||
                          emp.emp_code?.toLowerCase().includes(employeeSearchTerm.toLowerCase())
                        )
                        .map(employee => (
                          <div
                            key={employee.id}
                            className="flex items-center p-3 hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                          >
                            <input
                              type="checkbox"
                              checked={selectedEmployees.includes(employee.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedEmployees([...selectedEmployees, employee.id]);
                                } else {
                                  setSelectedEmployees(selectedEmployees.filter(id => id !== employee.id));
                                }
                              }}
                              className="w-4 h-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                            />
                            <div className="ml-3">
                              <p className="text-sm font-medium text-gray-900">{employee.full_name}</p>
                              <p className="text-xs text-gray-500">{employee.emp_code}</p>
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>

                  {/* Advance Details */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">Advance Details</h3>

                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Festival Advance Amount (₹) *
                        </label>
                        <input
                          type="number"
                          value={festivalAdvanceForm.amount}
                          onChange={(e) => setFestivalAdvanceForm(prev => ({ ...prev, amount: e.target.value }))}
                          className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                          placeholder="Enter advance amount"
                          min="0"
                          step="100"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Monthly Recovery Amount (₹) *
                        </label>
                        <input
                          type="number"
                          value={festivalAdvanceForm.monthly_recovery}
                          onChange={(e) => setFestivalAdvanceForm(prev => ({ ...prev, monthly_recovery: e.target.value }))}
                          className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                          placeholder="Enter monthly recovery amount"
                          min="0"
                          step="100"
                        />
                      </div>

                      {festivalAdvanceForm.amount && festivalAdvanceForm.monthly_recovery && (
                        <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg">
                          <p className="text-sm text-purple-700">
                            <strong>Calculated Duration:</strong> {Math.ceil(parseFloat(festivalAdvanceForm.amount) / parseFloat(festivalAdvanceForm.monthly_recovery))} months
                          </p>
                        </div>
                      )}

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Purpose
                        </label>
                        <input
                          type="text"
                          value={festivalAdvanceForm.purpose}
                          onChange={(e) => setFestivalAdvanceForm(prev => ({ ...prev, purpose: e.target.value }))}
                          className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                          placeholder="e.g., Diwali Advance, Festival Advance"
                        />
                      </div>

                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                        <div className="flex items-start">
                          <AlertTriangle className="w-5 h-5 text-yellow-600 mr-2 mt-0.5" />
                          <div>
                            <h4 className="font-medium text-yellow-900 text-sm">Important</h4>
                            <p className="text-xs text-yellow-700 mt-1">
                              Festival advances will be created in <strong>draft status</strong>. You can review and activate them using the "Activate Festival Advances" button.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-3 p-6 border-t border-gray-200">
                <button
                  onClick={() => {
                    setShowFestivalAdvanceModal(false);
                    setSelectedEmployees([]);
                    setEmployeeSearchTerm('');
                  }}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleApplyFestivalAdvance}
                  disabled={selectedEmployees.length === 0 || !festivalAdvanceForm.amount || !festivalAdvanceForm.monthly_recovery}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Create Festival Advances ({selectedEmployees.length})
                </button>
              </div>
            </motion.div>
          </div>
        </AnimatePresence>
      )}
    </div>
  );
};

export default LoansAdvancesManagement;
